import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  comparableNumber,
  ConfirmResult,
  currencySignSelector,
  IChangedDataItem,
  ICompareResult,
  IRideDto,
  Log,
  RideStatusEnum,
  RydeUiUnit,
  SINGLE_KEY,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { useDropdownSourceContext } from "../../contexts";
import { useModalConfirmsRide } from "./use-modal-confirms-ride";

export const useRideEditCompareResults = () => {
  const { t } = useTranslation();
  const currencySign = useRecoilValue(currencySignSelector);
  const { confirmDataChangedAction } = useModalConfirmsRide();
  const { getDropdownLabel } = useDropdownSourceContext();
  const { hasPermissionDontSeeRideChangesPopup, hasPermissionViewPrices } =
    useHaveCustomPermissions();

  const compareRidesDialogResult = useEvent(
    async ({
      rideFromResponse,
      rideSnapshot,
      rideDto,
      skipChangedValuesConfirmation,
    }: {
      rideFromResponse: Partial<IRideDto>;
      rideSnapshot: Partial<IRideDto>;
      rideDto: Partial<IRideDto> | undefined;
      skipChangedValuesConfirmation: boolean | undefined;
    }): Promise<ICompareResult> => {
      const changesList: IChangedDataItem[] = [];

      const addChangeIfExists = async (
        field: keyof IRideDto,
        dropdownEntityName: RydeUiUnit,
      ): Promise<void> => {
        if (
          comparableNumber(rideFromResponse[field]) !==
          comparableNumber(rideSnapshot[field])
        ) {
          Log.debug(
            `${dropdownEntityName} changed ids: `,
            rideFromResponse[field],
            rideSnapshot[field],
          );

          changesList.push({
            oldValue: rideSnapshot[field] ? +rideSnapshot[field] : null,
            newValue: rideFromResponse[field] ? +rideFromResponse[field] : null,
            oldLabel: await getDropdownLabel(
              dropdownEntityName,
              rideSnapshot[field] as number,
            ),
            newLabel: await getDropdownLabel(
              dropdownEntityName,
              rideFromResponse[field] as number,
            ),
            fieldName: field,
            label: capitalize(
              t(`ride.ENTITY_CHANGED`, {
                entity: t(`${dropdownEntityName}.${SINGLE_KEY}`),
              }),
            ),
          });
        }
      };

      await addChangeIfExists("siteId", "site");
      await addChangeIfExists("supplierId", "supplier");
      await addChangeIfExists("vehicleTypeId", "vehicleType");

      if (hasPermissionViewPrices) {
        await addChangeIfExists("priceRateId", "priceRate");
      }

      if (
        hasPermissionViewPrices &&
        comparableNumber(rideFromResponse.priceRateId) !==
          comparableNumber(rideSnapshot.priceRateId)
      ) {
        const newLabelPrice =
          rideSnapshot.price === rideFromResponse.price
            ? ""
            : `${currencySign} ${rideFromResponse.price ?? 0}`;
        const oldLabelPrice =
          rideSnapshot.price === rideFromResponse.price
            ? ""
            : `${currencySign} ${rideSnapshot.price ?? 0}`;
        const labelPrice =
          rideSnapshot.price === rideFromResponse.price
            ? t("ride.PRICE_WILL_NOT_CHANGE")
            : t("ride.ENTITY_CHANGED", {
                entity: t(`general.PRICE_KEY`),
              });
        changesList.push({
          newValue: null,
          oldValue: null,
          oldLabel: oldLabelPrice,
          newLabel: newLabelPrice,
          fieldName: "priceRateId",
          label: labelPrice,
        });
      }

      const { supplierId, vehicleTypeId, priceRateId } = rideSnapshot || {};

      if (
        rideDto?.status === RideStatusEnum.New &&
        rideFromResponse.status === RideStatusEnum.Draft
      ) {
        changesList.push({
          newValue: null,
          oldValue: null,
          oldLabel: t("selectListLabel.New"),
          newLabel: t("selectListLabel.Draft"),
          fieldName: "status",
          label: t("ride.ENTITY_CHANGED", {
            entity: t(`ride.gridLabel.status`),
          }),
        });
      }

      if (!changesList.length) {
        Log.debug(
          "No changes for price-critical fields  on dry save from api, dry run results not applied to form",
        );
        return { confirmed: true };
      }

      if (!supplierId && !vehicleTypeId && !priceRateId) {
        Log.debug(
          "Applying dry-run changes without confirm dialog: no supplier & vehicle type & priceRate values on form",
          changesList,
        );
        return { confirmed: true, changesList };
      }

      if (hasPermissionDontSeeRideChangesPopup) {
        Log.debug(
          "Applying dry-run changes without confirm dialog:  permission allows skipping ride changes popup",
          changesList,
        );
        return { confirmed: true, changesList };
      }

      if (skipChangedValuesConfirmation) {
        Log.debug(
          "Applying dry-run changes without confirm dialog: skipChangedValuesConfirmation flag is set (e.g., drag-n-drop stations)",
          changesList,
        );
        return { confirmed: true, changesList };
      }

      Log.debug("Changes list on dry save to be confirmed: ", changesList);
      const confirmResult = await confirmDataChangedAction(changesList);

      if (confirmResult === ConfirmResult.OK) {
        return { confirmed: true, changesList };
      }

      return { confirmed: false };
    },
  );
  return { compareRidesDialogResult };
};
